import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../../styles/allusers.scss";
import moment from "moment";
import ProfileModal from "../modal/profile-modal";
import { Link } from "react-router-dom";
import DataTableBase from "../common/Datatable";
import { enumToString, isAccessAllowed } from "../../../utils/helpers";
import TrainerContext from "../../../context/trainerContext/TrainerContext";
import Select from "react-select";
import InputSearch from "../common/InputSearch";
import makeRequest from "../../../api";
import {
  ringService,
  subscriptionService,
  trainerService,
  userService,
} from "../../../api/services";
import { toast } from "react-toastify";

const issueBasedGoals = ["PRE_DIABETES_REVERSAL", "DIABETES_REVERSAL"];
const healthGoalsOptions = [
  {
    value: "STAY_FIT",
    label: "Stay fit",
  },
  {
    value: "WEIGHT_LOSS",
    label: "Weight loss",
  },
  {
    value: "HEALTHY_SKIN",
    label: "Health skin",
  },
  {
    value: "DIABETES_REVERSAL",
    label: "Diabetes reversal",
  },
  {
    value: "PRE_DIABETES_REVERSAL",
    label: "Pre-diabetes reversal",
  },
  {
    value: "GUT_HEALTH",
    label: "Gut health",
  },
];
const gabitOptions = [
  {
    value: "NUTRITION",
    label: "Nutrition",
  },
  {
    value: "FITNESS",
    label: "Fitness",
  },
];

const AllUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { getTrainerState } = useContext(TrainerContext);

  const trainerData = getTrainerState();
  const { trainerId, trainerCategory } = trainerData;

  const [searchedUsers, setSearchedUsers] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [currentPageData, setCurrentPageData] = useState<any[]>([]);

  const [viewProfile, setViewProfile] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserData, setSelectedUserData] = useState<any>({});
  const [filters, setFilters] = useState<{ [key: string]: any }>({
    healthGoal: [],
    searchedTerm: "",
    gabit: null,
  });
  const gabit = filters?.gabit?.value;

  const isUserSessionsViewAllowed = isAccessAllowed("USER_SESSIONS_VIEW");
  const isMealPlanViewAllowed = isAccessAllowed("MEAL_PLAN_VIEW");
  const isRingDataViewAllowed = isAccessAllowed("VIEW_RING_USERS_DATA");
  const isCGMDataViewAllowed = isAccessAllowed("VIEW_CGM_USERS_DATA");
  const isReportsViewAllowed = isAccessAllowed("REPORT_VIEW");
  const isNotesViewAllowed = isAccessAllowed("NOTES_VIEW");
  const isViewAllUsersAllowed = isAccessAllowed("VIEW_ALL_USERS");
  const isAssessmentViewAllowed = isAccessAllowed("ASSESSMENT_VIEW");

  useEffect(() => {
    const healthGoals = searchParams?.get("healthGoal");
    const healthGoalsArray = healthGoals?.split(",");
    const healthGoalsMap: any = {};
    healthGoalsArray?.map((item: any) => (healthGoalsMap[item] = 1));

    const searchedTerm = searchParams?.get("searchedTerm");
    const gabit = searchParams?.get("gabit");
    const gabitObj = gabitOptions?.find((item) => item?.value === gabit);

    let paramsObj: any = {
      gabit:
        gabitOptions?.find((item: any) => item?.value === trainerCategory) ||
        gabitOptions[0],
    };

    if (healthGoals) {
      paramsObj = {
        ...paramsObj,
        healthGoal: healthGoalsOptions?.filter(
          (item: any) => healthGoalsMap[item?.value]
        ),
      };
    }
    if (gabitObj) {
      paramsObj = {
        ...paramsObj,
        gabit: gabitObj,
      };
    }
    if (searchedTerm) {
      paramsObj = {
        ...paramsObj,
        searchedTerm,
      };
    }

    setFilters({ ...paramsObj });
  }, [trainerCategory]);

  useEffect(() => {
    if (gabit && trainerCategory) {
      (async () => {
        let res;
        if (trainerCategory === "ADMIN" || isViewAllUsersAllowed) {
          try {
            const response = await makeRequest(
              subscriptionService.getAllSubscriptionUsers({
                gabit: gabit,
              })
            );
            res = response?.data?.subscriptions || [];
          } catch (error: any) {
            toast.error("Error fetching all users!");
          }
        } else {
          try {
            const response = await makeRequest(
              trainerService.getBulkUsersForTrainer({
                trainerId,
                gabit,
              })
            );
            res = response?.data?.body?.data || [];
          } catch (error: any) {
            toast.error("Error fetching all users!");
          }
        }

        const usersList = res.sort(
          (a: any, b: any) => b.startDate - a.startDate
        );
        setUsers([...usersList]);
      })();
    }
  }, [gabit, trainerCategory]);

  useEffect(() => {
    const filteredUsers = getFilteredUsers({
      users: users,
      filters: filters,
    });
    setSearchedUsers([...filteredUsers]);
  }, [users, filters]);

  const getFilteredUsers = ({
    users,
    filters,
  }: {
    users: Array<any>;
    filters: { [key: string]: any };
  }) => {
    let x = users;
    const searchedTerm = filters?.searchedTerm?.toLowerCase()?.trim();
    if (searchedTerm?.length > 0) {
      x = x?.filter(
        (item) =>
          item?.userId?.toLowerCase()?.includes(searchedTerm) ||
          item?.name?.toLowerCase()?.includes(searchedTerm)
      );
    }
    if (filters?.healthGoal?.length > 0) {
      x = x?.filter((item) =>
        filters?.healthGoal
          ?.map((item: any) => item?.value)
          ?.includes(item?.healthGoal)
      );
    }
    return x;
  };

  useEffect(() => {
    const filteredUsers = getFilteredUsers({ users, filters });

    let queryObj = {};

    Object.keys(filters)?.forEach((item: any) => {
      if (item === "healthGoal") {
        if (filters?.healthGoal?.length > 0) {
          queryObj = {
            ...queryObj,
            healthGoal: filters?.healthGoal
              ?.map((item: any) => item?.value)
              ?.join(","),
          };
        }
      } else if (item === "gabit") {
        queryObj = {
          ...queryObj,
          gabit: filters?.gabit?.value,
        };
      } else {
        if (filters[item]) {
          queryObj = {
            ...queryObj,
            [item]: filters[item],
          };
        }
      }
    });

    setSearchParams({ ...queryObj });
    setSearchedUsers([...filteredUsers]);
  }, [filters]);

  const getRingUsersIDs = async () => {
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, searchedUsers?.length);
    const currentData = searchedUsers?.slice?.(startIndex, endIndex);
    let updatedData = [...currentData];

    const usersList = currentData?.map?.((data: any) => data?.userId);
    if (!usersList?.length) {
      return;
    }
    try {
      const res = await makeRequest(
        ringService.getRingUserFromUserList({
          users: usersList,
        })
      );
      const cgmRes = await makeRequest(
        userService.getCGMUsersFromList({
          users: usersList,
        })
      );
      if (res?.data?.body && res?.data?.body?.ringUsers?.length) {
        const ringUsers = res?.data?.body?.ringUsers;
        const updatedCurrentPageData: any = updatedData?.map?.((data: any) => {
          const isRingUser = ringUsers?.includes(data?.userId);
          return {
            ...data,
            ...(isRingUser && {
              isRingUser: true,
            }),
          };
        });
        updatedData = updatedCurrentPageData;
      }
      if (cgmRes?.data?.body && cgmRes?.data?.body?.cgmUsers?.length) {
        const cgmUsers = cgmRes?.data?.body?.cgmUsers;
        const updatedCurrentPageData: any = updatedData?.map?.((data: any) => {
          const isCGMUser = cgmUsers?.includes(data?.userId);
          return {
            ...data,
            ...(isCGMUser && {
              isCGMUser: true,
            }),
          };
        });
        updatedData = updatedCurrentPageData;
      }
    } catch (error: any) {
      toast.error(error.message);
    }
    setCurrentPageData([...updatedData]);
  };

  useEffect(() => {
    if (searchedUsers?.length) {
      getRingUsersIDs();
    }
  }, [searchedUsers, currentPage, perPage]);

  const handlePageChange = (page: any) => {
    setCurrentPage(page);
  };

  const isIssueBasedPlan = (goal: string) => {
    if (
      goal === "PRE_DIABETES_REVERSAL" ||
      goal === "DIABETES_REVERSAL" ||
      goal === "GUTHEALTH" ||
      goal === "CHOLESTROL" ||
      goal === "HYPERTENSION"
    ) {
      return true;
    }
    return false;
  };

  const columns = useMemo(
    () => [
      {
        name: "User Name",
        selector: (row: any) => row.name,
        cell: (row: any) => {
          return <div>{row?.name}</div>;
        },
        sortable: true,
        width: "22.5%",
      },
      {
        name: "Plan dates",
        selector: (row: any) => row.startDate,
        cell: (row: any) => {
          return (
            <div className="">
              {moment(row?.startDate).format("DD MMM YY")} -
              {moment(row?.endDate).format("DD MMM YY")}
            </div>
          );
        },
        sortable: true,
        width: "16%",
      },
      {
        name: "Health goal",
        selector: (row: any) => row.healthGoal,
        cell: (row: any) => {
          return <div>{enumToString(row?.healthGoal)}</div>;
        },
        width: "17.5%",
      },
      {
        name: "Plan status",
        selector: (row: any) => row.healthGoal,
        cell: (row: any) => {
          return (
            <div>
              {issueBasedGoals.includes(row?.healthGoal) ? (
                <div>
                  <span style={{ color: "red" }}>
                    {(row?.issueBasedMealPlanStatus === null ||
                      row?.issueBasedMealPlanStatus === "NEW_USER") &&
                      "Doctor's approval awaited"}
                  </span>
                  <span style={{ color: "blue" }}>
                    {row?.issueBasedMealPlanStatus === "APPROVED_FROM_DOCTOR" &&
                      "Approved by Doctor"}
                  </span>
                  <span style={{ color: "green" }}>
                    {row?.issueBasedMealPlanStatus === "MEAL_PLAN_GENERATED" &&
                      "Plan generated"}
                  </span>
                </div>
              ) : (
                "-"
              )}
            </div>
          );
        },
        width: "15%",
      },
      {
        name: "Actions",
        selector: (row: any) => row,
        cell: (row: any) => {
          return (
            <div>
              {isMealPlanViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/meal-plan`}
                  state={row}
                  className="btn btn-sm btn-outline-success m-1"
                >
                  Plan
                </Link>
              )}
              {isRingDataViewAllowed && row?.isRingUser && (
                <Link
                  to={`/users/${row?.userId}/ring-details`}
                  state={row}
                  className="btn btn-sm btn-outline-danger m-1"
                >
                  Ring Data
                </Link>
              )}
              {isCGMDataViewAllowed && row?.isCGMUser && (
                <Link
                  to={`/users/${row?.userId}/cgm-details`}
                  state={row}
                  className="btn btn-sm btn-outline-secondary m-1"
                >
                  CGM Data
                </Link>
              )}
              {isNotesViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/notes`}
                  state={row}
                  className="btn btn-sm btn-outline-primary m-1"
                >
                  Notes
                </Link>
              )}
              {isIssueBasedPlan(row?.healthGoal) && isReportsViewAllowed ? (
                <Link
                  to={`/users/${row?.userId}/reports`}
                  state={row}
                  className="btn btn-sm btn-outline-danger m-1"
                >
                  Reports
                </Link>
              ) : null}
              {isUserSessionsViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/sessions`}
                  state={row}
                  className="btn btn-sm btn-outline-dark m-1"
                >
                  Sessions
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    [gabit, trainerData]
  );
  const fitnessColumns = useMemo(
    () => [
      {
        name: "User Name",
        selector: (row: any) => row.name,
        cell: (row: any) => {
          return <div>{row?.name}</div>;
        },
        sortable: true,
        width: "25%",
      },
      {
        name: "Plan dates",
        selector: (row: any) => row.startDate,
        cell: (row: any) => {
          return (
            <div className="">
              {moment(row?.startDate).format("DD MMM YY")} -
              {moment(row?.endDate).format("DD MMM YY")}
            </div>
          );
        },
        sortable: true,
        width: "20%",
      },
      {
        name: "Health goal",
        selector: (row: any) => row.healthGoal,
        cell: (row: any) => {
          return <div>{enumToString(row?.healthGoal)}</div>;
        },
        width: "20%",
      },
      {
        name: "Actions",
        selector: (row: any) => row,
        cell: (row: any) => {
          return (
            <div>
              {isNotesViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/notes`}
                  state={row}
                  className="btn btn-sm btn-outline-primary m-1"
                >
                  Notes
                </Link>
              )}
              {isAssessmentViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/assessments`}
                  state={row}
                  className="btn btn-sm btn-outline-dark m-1"
                >
                  Assessment
                </Link>
              )}
              {isRingDataViewAllowed && row?.isRingUser && (
                <Link
                  to={`/users/${row?.userId}/ring-details`}
                  state={row}
                  className="btn btn-sm btn-outline-danger m-1"
                >
                  Ring Data
                </Link>
              )}
              {isUserSessionsViewAllowed && (
                <Link
                  to={`/users/${row?.userId}/sessions`}
                  state={row}
                  className="btn btn-sm btn-outline-dark m-1"
                >
                  Sessions
                </Link>
              )}
            </div>
          );
        },
        width: "35%",
      },
    ],
    [trainerData]
  );

  const handleChange = (values: any, name: string) => {
    if (name === "healthGoal") {
      setFilters((prev) => ({
        ...prev,
        healthGoal: values,
      }));
    }
    if (name === "gabit") {
      setFilters((prev) => ({
        ...prev,
        gabit: values,
      }));
    }
  };

  const handleSearchTermChange = (value: any) => {
    setFilters((prev) => ({
      ...prev,
      searchedTerm: value,
    }));
  };

  return (
    <div className="desktop-allusers container-lg">
      <ProfileModal
        userData={selectedUserData}
        open={viewProfile}
        setOpen={setViewProfile}
      />
      <>
        <div className="header d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-700 fs-24 mb-0">All users</h6>
          <InputSearch
            placeholder="Search user by name or userId"
            value={filters?.searchedTerm}
            setValue={handleSearchTermChange}
          />
        </div>
        <div className="mb-4">
          <h6>Filters: </h6>
          <div className="row">
            <div className="col-4">
              <Select
                onChange={(option: any) => {
                  handleChange(option, "gabit");
                }}
                value={filters?.gabit}
                className="basic-single"
                classNamePrefix="select"
                name="gabit"
                options={gabitOptions}
                placeholder="Select gabit"
                isDisabled={
                  trainerCategory === "FITNESS" ||
                  trainerCategory === "NUTRITION" ||
                  trainerCategory === "DOCTOR"
                }
              />
            </div>
            <div className="col-4">
              <Select
                isMulti
                onChange={(option: any) => {
                  handleChange(option, "healthGoal");
                }}
                value={filters?.healthGoal}
                className="basic-single"
                classNamePrefix="select"
                isClearable={true}
                isRtl={false}
                isSearchable={true}
                name="healthGoal"
                options={healthGoalsOptions}
                placeholder="Select health goal"
              />
            </div>
          </div>
        </div>

        {currentPageData?.length === 0 || searchedUsers?.length === 0 ? (
          <h6 className="mb-0">No user found.</h6>
        ) : (
          <DataTableBase
            data={currentPageData}
            columns={gabit === "FITNESS" ? fitnessColumns : columns}
            pagination
            paginationServer
            paginationTotalRows={searchedUsers?.length}
            paginationPerPage={perPage}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={(perPage) => setPerPage(perPage)}
          />
        )}
      </>
    </div>
  );
};

export default AllUsers;
